import { Box, Button, Card, CardBody, Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiEdit3, FiSlash, FiUserCheck } from "react-icons/fi";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  CompetitionStageDto,
  ParticipationRequestStatus,
  PortalLifecycleStatus,
  useCreateParticipationRequestMutation,
  useCreateBidMutation,
  useGetPortalMyBidsQuery,
  useGetMyParticipationQuery,
  useWithdrawParticipationRequestMutation,
  type ParticipationRequestDto,
  type ParticipationRequestRejectionDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { currentPathSuffix } from "../../../../common/auth/returnTo";
import { useApiError } from "../../../../common/errors/useApiError";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { useAppSelector } from "../../../../common/redux/hooks";
import { RejectionDetails } from "../../../../common/rejection/RejectionDetails";
import { urls } from "../../../../urls";
import { formatShortDate } from "../utils/displayFormatters";
import { usePortalEventUrls } from "../../../../common/sourcing-events/portal/usePortalEventUrls";
import { isDpsScheme, isSchemeProcedure } from "../../buyer/wizard/utils/procedure";
import { isDesignContest } from "../../buyer/wizard/utils/designContest";
import { continuousAdmissionUntil, effectiveDeadlineIso } from "../utils/effectiveDeadline";
import { ownPortalBid } from "../ownPortalBid";

// The entire CTA surface for two-stage competitions (begrenset anbudskonkurranse): request to
// participate while requests are open, status while the buyer reviews, and the invited-to-tender
// entry point once the invitation is confirmed. Single-stage events keep the register-interest +
// start-offer pair.
export const ParticipationCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t, i18n } = useTranslation();
  const designContest = isDesignContest(event.procedureType);
  const navigate = useNavigate();
  const location = useLocation();
  const portalUrls = usePortalEventUrls();
  const displayer = useApiError();
  const authState = useAppSelector((state) => state.authState.state);
  const loggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const stage = event.competitionStage;
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  const admissionUntil = continuousAdmissionUntil(event, effectiveDeadlineIso(event));
  const requestToParticipateBody = admissionUntil
    ? t("The system admits new suppliers until {{date}}. Apply to join to compete for its contracts.", {
        date: formatShortDate(admissionUntil, momentLocaleFor(i18n.language)),
      })
    : isDpsScheme(event.procedureType)
    ? t("Apply with an ESPD. Once qualified you are invited to every competition under the system.")
    : isSchemeProcedure(event.procedureType)
    ? t("Apply with an ESPD. Once qualified you are on the supplier list for competitions under the system.")
    : t("Submit a request to participate before the deadline to compete for the contract.");

  const [createRequest, { isLoading: isCreatingRequest }] = useCreateParticipationRequestMutation();
  const [createBid, { isLoading: isCreatingBid }] = useCreateBidMutation();
  const {
    data: myBids,
    isLoading: myBidsLoading,
    isError: myBidsFailed,
  } = useGetPortalMyBidsQuery({}, { skip: !loggedInWithOrg });
  const { data: mine, isLoading: mineLoading } = useGetMyParticipationQuery(
    { eventId: event.id },
    { skip: !loggedInWithOrg || !stage || !isOpen },
  );

  // Nothing else on the page says the system has closed for good.
  if (!stage && isOpen && isSchemeProcedure(event.procedureType)) {
    const validTo = event.dpsValidTo ?? event.qualificationSchemeValidTo;
    return (
      <Notice
        icon={FiSlash}
        tone="muted"
        title={
          isDpsScheme(event.procedureType)
            ? t("This dynamic purchasing system is closed")
            : t("This qualification system is closed")
        }
        body={
          validTo
            ? t("It ran until {{date}}. No new suppliers are admitted.", {
                date: formatShortDate(validTo, momentLocaleFor(i18n.language)),
              })
            : t("No new suppliers are admitted.")
        }
      />
    );
  }

  if (!stage || !isOpen || stage === CompetitionStageDto.InterestOpen) return null;

  const calledByPriorInformationNotice = event.plannedInterestInvitationAt != null;
  const onlyRegisteredSuppliers = (
    <Notice
      icon={FiSlash}
      tone="muted"
      title={t("Only invited suppliers can take part")}
      body={t("zz_SourcingEventApiErrorCode_NotInvitedToConfirmInterest")}
    />
  );

  if (!loggedInWithOrg) {
    if (stage !== CompetitionStageDto.RequestsOpen) return null;
    if (calledByPriorInformationNotice) return onlyRegisteredSuppliers;
    return (
      <Frame
        icon={FiUserCheck}
        title={
          isDpsScheme(event.procedureType)
            ? t("Want to join this dynamic purchasing system?")
            : isSchemeProcedure(event.procedureType)
            ? t("Want to join this qualification system?")
            : t("Want to participate in this procurement?")
        }
        body={requestToParticipateBody}
      >
        <Button
          as={RouterLink}
          to={urls.loginWithReturnUrl(currentPathSuffix(location))}
          variant="outline"
          colorScheme="gray"
          flexShrink="0"
        >
          {isSchemeProcedure(event.procedureType) ? t("Log in to apply to join") : t("Log in to request participation")}
        </Button>
      </Frame>
    );
  }

  if (mineLoading || !mine) return null;
  if (stage === CompetitionStageDto.RequestsOpen && !mine.mayRequestToParticipate && !mine.request) {
    return onlyRegisteredSuppliers;
  }
  const request = mine.request ?? null;
  const status = request?.status ?? null;

  const startOrContinueRequest = async () => {
    try {
      const target =
        request && status === ParticipationRequestStatus.Draft
          ? request
          : await createRequest({ eventId: event.id }).unwrap();
      navigate(portalUrls.request.edit.go(event.id, target.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const startOffer = async () => {
    try {
      const bid = await createBid({ eventId: event.id }).unwrap();
      navigate(portalUrls.bid.edit.go(event.id, bid.id).intro);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  if (stage === CompetitionStageDto.RequestsOpen) {
    // FOA § 26-6 / forsyningsforskriften § 13-2 answer each application on its own, so the
    // supplier sees its own outcome here.
    if (isSchemeProcedure(event.procedureType)) {
      const dps = isDpsScheme(event.procedureType);
      if (status === ParticipationRequestStatus.Submitted) {
        return (
          <Notice
            icon={FiCheckCircle}
            tone="success"
            title={t("Application received – under assessment")}
            body={
              dps
                ? t("The buyer has 10 working days to decide. You will be told either way.")
                : t(
                    "The buyer normally decides within four months, and tells you within two if it needs longer. You will be told either way.",
                  )
            }
          />
        );
      }
      if (status === ParticipationRequestStatus.Qualified && request) {
        return (
          <Frame
            icon={FiCheckCircle}
            title={t("You are qualified in this system")}
            body={
              dps
                ? t("You will be invited to every competition started under the system from now on.")
                : t("You are on the supplier list and may be invited to competitions under the system.")
            }
          >
            <WithdrawButton requestId={request.id} />
          </Frame>
        );
      }
      if (status === ParticipationRequestStatus.Removed && request?.stillAdmittedUntil) {
        return (
          <Frame
            icon={FiCheckCircle}
            title={t("You are on the supplier list up to and including {{date}}", {
              date: formatShortDate(
                new Date(Date.parse(request.stillAdmittedUntil) - 1).toISOString(),
                momentLocaleFor(i18n.language),
              ),
            })}
            body={
              request.removalReason ??
              t("The buyer has decided to remove you from the supplier list. Until then you may still be invited.")
            }
          />
        );
      }
      const closedOutcome =
        status === ParticipationRequestStatus.Rejected ||
        status === ParticipationRequestStatus.Withdrawn ||
        status === ParticipationRequestStatus.Removed;
      const schemeClosed = closedOutcome && !dps ? qualificationSchemeOutcome(status, request, t) : null;
      const rejectionLetter =
        status === ParticipationRequestStatus.Rejected ? request?.qualificationVerdict?.rejection : null;
      return (
        <Stack spacing="4">
          <Frame
            icon={FiUserCheck}
            followUp={schemeClosed?.followUp}
            title={
              schemeClosed
                ? schemeClosed.title
                : status === ParticipationRequestStatus.Rejected
                  ? t("You were not qualified in this dynamic purchasing system")
                  : closedOutcome
                    ? t("You are no longer in this system")
                    : dps
                    ? t("Want to join this dynamic purchasing system?")
                    : t("Want to join this qualification system?")
            }
            body={
              schemeClosed
                ? schemeClosed.body
                : closedOutcome
                  ? request?.qualificationVerdict?.rejectionReason ??
                    t("You can apply again for as long as the system runs.")
                  : dps
                  ? t("Apply with an ESPD. Once qualified you are invited to every competition under the system.")
                  : t("Apply with an ESPD. Once qualified you are on the supplier list for competitions under the system.")
            }
          >
            <Button
              colorScheme="teal"
              leftIcon={<Icon as={FiEdit3} />}
              onClick={startOrContinueRequest}
              isLoading={isCreatingRequest}
              flexShrink={0}
            >
              {status === ParticipationRequestStatus.Draft
                ? t("Continue your application")
                : closedOutcome
                  ? t("Apply again")
                  : t("Apply to join")}
            </Button>
          </Frame>
          {rejectionLetter && <RejectionLetter rejection={rejectionLetter} />}
        </Stack>
      );
    }
    if (status === ParticipationRequestStatus.Submitted) {
      return (
        <Notice
          icon={FiCheckCircle}
          tone="success"
          title={t("Your request to participate has been submitted")}
          body={designContest
            ? t("The buyer reviews all requests after the deadline. Qualified suppliers get the invitation to submit an entry, with the deadline for entries.")
            : t("The buyer reviews all requests after the deadline. Qualified suppliers get the invitation to tender with the bid deadline.")}
        />
      );
    }
    return (
      <Frame
        icon={FiUserCheck}
        title={t("Want to participate in this procurement?")}
        body={requestToParticipateBody}
      >
        <Button
          colorScheme="teal"
          leftIcon={<Icon as={FiEdit3} />}
          onClick={startOrContinueRequest}
          isLoading={isCreatingRequest}
          flexShrink={0}
        >
          {status === ParticipationRequestStatus.Draft
            ? t("Continue your request")
            : t("Request to participate")}
        </Button>
      </Frame>
    );
  }

  if (stage === CompetitionStageDto.Qualification) {
    // Verdicts are masked server-side until the invitation is confirmed, so any non-draft
    // request reads as Submitted here.
    if (status != null && status !== ParticipationRequestStatus.Draft) {
      return (
        <Notice
          icon={FiCheckCircle}
          tone="info"
          title={t("Your request is under review")}
          body={designContest
            ? t("Qualified suppliers get the invitation to submit an entry, with the deadline for entries.")
            : t("Qualified suppliers get the invitation to tender with the bid deadline.")}
        />
      );
    }
    return (
      <Notice
        icon={FiSlash}
        tone="muted"
        title={t("The deadline for requests to participate has passed")}
        body={designContest
          ? t("Only suppliers who requested participation in time can be invited to submit an entry.")
          : t("Only suppliers who requested participation in time can be invited to tender.")}
      />
    );
  }

  // TENDERING
  if (mine.invited) {
    if (myBidsLoading || ownPortalBid(myBids?.bids, event.id)) return null;
    if (myBidsFailed && !myBids) {
      return (
        <Notice
          icon={FiSlash}
          tone="muted"
          title={designContest
            ? t("We could not check whether you have delivered an entry")
            : t("We could not check whether you have delivered an offer")}
          body={t("Reload the page to try again.")}
        />
      );
    }
    return (
      <Card variant="filled" bg="smPrimarySofter" rounded="xl">
        <CardBody>
          <Flex direction={{ base: "column", md: "row" }} align={{ md: "center" }} justify="space-between" gap="4">
            <Stack spacing="1">
              <Text fontWeight="semibold">{designContest
                ? t("You are invited to submit an entry")
                : t("You are invited to tender")}</Text>
              <Text fontSize="sm" color="smMuted">
                {designContest
                  ? t("Build your entry step by step. We save your progress automatically.")
                  : t("Build your offer step by step. We save your progress automatically.")}
              </Text>
            </Stack>
            <Button
              colorScheme="teal"
              leftIcon={<Icon as={FiEdit3} />}
              onClick={startOffer}
              isLoading={isCreatingBid}
              flexShrink={0}
            >
              {designContest ? t("Start entry") : t("Start offer")}
            </Button>
          </Flex>
        </CardBody>
      </Card>
    );
  }
  if (request?.selectionOutcome != null && !request.selectionOutcome.selected) {
    // § 16-12 (4): qualified, but not among the selected — the announced cap applied.
    return (
      <Notice
        icon={FiSlash}
        tone="muted"
        title={designContest
          ? t("Qualified, but not selected to submit an entry")
          : t("Qualified, but not selected for tender")}
        body={
          request.selectionOutcome.reason ??
          (designContest
            ? t("Your organization was qualified, but not among the suppliers invited to submit an entry.")
            : t("Your organization was qualified, but not among the suppliers invited to tender (FOA § 16-12)."))
        }
      />
    );
  }
  if (status === ParticipationRequestStatus.Rejected) {
    const rejection = request?.qualificationVerdict?.rejection;
    return (
      <Stack spacing="4">
        <Notice
          icon={FiSlash}
          tone="muted"
          title={t("Your request to participate was not qualified")}
          body={request?.qualificationVerdict?.rejectionReason ?? t("The buyer did not qualify your organization for this competition.")}
        />
        {/* The § 24-10 meddelelse itself: the hjemler invoked and the buyer's message, including
            klageadgang. No karensperiode runs on a rejection — that is tildeling only. */}
        {rejection && <RejectionLetter rejection={rejection} />}
      </Stack>
    );
  }
  return (
    <Notice
      icon={FiSlash}
      tone="muted"
      title={designContest
        ? t("The entry phase is open for invited suppliers")
        : t("The tender phase is open for invited suppliers")}
      body={designContest
        ? t("Only suppliers who were qualified in the request phase can submit an entry in this design contest.")
        : t("Only suppliers who were qualified in the request phase can tender in this competition.")}
    />
  );
};

// Leaving an ordning is the supplier's own call and always allowed (FOA § 26-6). Applying again
// later is too, which is why this is a plain action rather than a warning-laden confirmation.
const WithdrawButton = ({ requestId }: { requestId: string }) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [withdraw, { isLoading }] = useWithdrawParticipationRequestMutation();

  return (
    <Button
      variant="outline"
      colorScheme="gray"
      isLoading={isLoading}
      flexShrink="0"
      onClick={async () => {
        try {
          await withdraw({ requestId }).unwrap();
        } catch (e) {
          displayer.trigger(e);
        }
      }}
    >
      {t("Leave the system")}
    </Button>
  );
};

const qualificationSchemeOutcome = (
  status: ParticipationRequestStatus | undefined,
  request: ParticipationRequestDto | null | undefined,
  t: TFunction,
): { title: string; body: string; followUp?: string } => {
  const applyAgain = t("You can apply again for as long as the qualification system runs.");
  if (status === ParticipationRequestStatus.Rejected) {
    return {
      title: t("You were not qualified in this qualification system"),
      body: request?.qualificationVerdict?.rejectionReason ?? applyAgain,
    };
  }
  const reason = status === ParticipationRequestStatus.Removed ? request?.removalReason : null;
  return {
    title: t("You are no longer on the supplier list"),
    ...(reason ? { body: reason, followUp: applyAgain } : { body: applyAgain }),
  };
};

const RejectionLetter = ({ rejection }: { rejection: ParticipationRequestRejectionDto }) => (
  <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="xl" px={{ base: "4", md: "6" }} py="4">
    <RejectionDetails grounds={rejection.grounds} message={rejection.message} />
  </Box>
);

export const Frame = ({
  icon,
  title,
  body,
  followUp,
  children,
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  followUp?: string;
  children?: React.ReactNode;
}) => (
  <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="xl" px={{ base: "4", md: "6" }} py="4">
    <HStack spacing="4" justify="space-between" align="center" flexWrap="wrap" rowGap="3">
      <HStack spacing="3" align="center">
        <Icon as={icon} boxSize="5" color="smPrimary" flexShrink="0" />
        <Stack spacing="0">
          <Text fontWeight="semibold">{title}</Text>
          <Text fontSize="sm" color="smMuted">
            {body}
          </Text>
          {followUp && (
            <Text fontSize="sm" color="smMuted">
              {followUp}
            </Text>
          )}
        </Stack>
      </HStack>
      {children}
    </HStack>
  </Box>
);

const Notice = ({
  icon,
  tone,
  title,
  body,
}: {
  icon: React.ElementType;
  tone: "success" | "info" | "muted";
  title: string;
  body: string;
}) => (
  <Box
    borderWidth="1px"
    borderColor={tone === "success" ? "smSuccessSoftBorder" : "smBorder"}
    bg={tone === "success" ? "smSuccessSoft" : tone === "info" ? "smPrimarySofter" : "smBackground"}
    rounded="xl"
    px={{ base: "4", md: "6" }}
    py="4"
  >
    <HStack spacing="3" align="center">
      <Icon
        as={icon}
        boxSize="5"
        color={tone === "success" ? "smSuccess" : tone === "info" ? "smPrimary" : "smMuted"}
        flexShrink="0"
      />
      <Stack spacing="0">
        <Text fontWeight="semibold">{title}</Text>
        <Text fontSize="sm" color="smMuted">
          {body}
        </Text>
      </Stack>
    </HStack>
  </Box>
);
